/** Copyright 2025 Midas Healthcare Solutions - All Rights Reserved **/
import { CollectionBinType } from './collection-bin.model';
import { FormularyWitnessRequirement, MedicationGroup } from './formulary.model';
import { Patient } from './patient.model';
import { RulePriority } from './rx-intercept-rule.model';
import { IWorkflow } from './workflow.model';
import { TranslocoService } from '@ngneat/transloco';

export enum WastingReason {
  ExpectedWaste = 1,
  Refused = 2,
  OrderChanged = 4,
  Dropped = 8,
  Others = 16,
  Override = 32,
  FoundMedication = 64,
}

export enum WorkFlowStatus {
  Created = 0,
  Completed = 1,
  Pending = 2,
  Cancelled = 3,
  Interrupted = 4,
}

export enum VIEWMode {
  Portrait = 0,
  Landscape = 1,
}

export const WorkFlowRequiresAttention = (status: WorkFlowStatus) =>
  status === WorkFlowStatus.Pending;

// update the values when we re-do formulary so that others have the largest value
export enum MedicationForm {
  Tablet = 1,
  Suppository = 2,
  Film = 4,
  Patch = 8,
  Capsule = 16,
  Ampule = 32,
  Vial = 64,
  Syringe = 128,
  Liquid = 256,
  Inhaler = 512,
  Bag = 2048,
  Others = 1024,
}

export interface Wasting extends IWorkflow {
  wasteAmount: number;
  wasteUnits: string | null;
  reasonForWaste: WastingReason | null;
  otherComments: string | null;
  otherReasons: string | null;
  wastingVideoFilePath: string;
  wastingVideoDuration: string;
  witnessVideoFilePath: string;
  screenVideoFilePath: string;
  screenRecordingVideoUploadComplete: boolean;
  wastingTime: string;
  locationId: number;
  locationName: string;
  cartId: number;
  cartName: string;

  wasteUserId: number;
  wasteUserName: string;
  witnessUserId: number | null;
  witnessUserName: string | null;

  toBeCollected: CollectedStatus;
  wasCollected: boolean;
  sentToInvestigation: boolean;
  workflowStatus: WorkFlowStatus; // replace with WorkflowStatus in IWorkflow eventually
  witnessRequirement: FormularyWitnessRequirement | null;
  witnessType: FormularyWitnessRequirement | null;
  viewMode: VIEWMode;
  binTypeUsed: CollectionBinType;

  wastingType: WastingType;
  standardWastingType: StandardWastingType;
  sampleId: number | null;
  sampleIdentifier: string;
  samplePriority: RulePriority;

  // below fields are only used for cancelled or auto-logged out wasting where patient or medication has been selected
  // for non-exception wastings
  selectedPatientID: number | null;
  selectedPatient: Patient | null;
  selectedMedicationGroupId: number | null;
  selectedMedicationGroup: MedicationGroup | null;

  // below fields are only used for Misc Wastes
  genericName: string | null;
  medicationDescription: string | null;
  isHazardous: boolean;
  isLiquid: boolean;
  requiresExitSafe: boolean;
  sessionId: string | null;

  exitSafeIdentifier: string | null;
  overrideExitSafe: boolean;

  lot: Lot | null;

  witnessVideoMetadata: string | null;
  aiVideoReview: AIVideoReview | null;

  flaggings: Flagging[];
}

export interface Lot {
  lotNumber: string | null;
  expiryDate: string | null;
  manufacturer: string | null;
  quantity: number;
  wasteAmount: number[];
  selectedRxIntercept: number | null;
}

export interface QueryWastingsRequest {
  numberOfRecords: number;
  startingIndex: number;
  searchString?: string;
  locations?: number[];
  carts?: number[];
  patients?: number[];
  wasters?: number[];
  witnesses?: number[];
  medicationGroups?: number[];
  endDate?: string | null;
  startDate?: string | null;
  statuses: WorkFlowStatus[];
  wastingTypes: WastingType[];
  showUserWastes?: boolean;
  transportId?: number;
  showOnlyStaleWastes?: boolean;
  showOnlyInterceptedWastes?: boolean;
  sortByDateOldestFirst?: boolean;
  sentToInvestigation?: boolean;
  flagTypes?: FlagType[];
  includeActiveFlagWastes?: boolean;
  onlyResolve?: boolean;

  // For Alibi display, do not update count numbers
  silentUpdate?: boolean;
}

export enum WastingPageType {
  AnytimeWitness,
  ReviewRequired,
  FlaggedWastings,
  FlaggedByAI,
  FlaggedByWitness,
  FlaggedBySystem,
  HistoricalWastes,
  FinalWasteAnytimeWitness,
  FinalWasteHistoricalWastes,
  SentToInvestigationHistoricalWastes,
  SentToInvestigationFinalWasteHistoricalWastes
}

export enum WastingType {
  Standard,
  Final,
}

export enum CollectedStatus {
  No,
  Yes,
  Skipped,
}

export enum StandardWastingType {
  Default,
  Compound,
  Expired,
  Miscellaneous,
  PatientOwn,
  NoPatientBatch,
  NoPatientExpired,
  NoPatientMiscellaneous,
  FoundMed,
}

export interface LandingPageSummaryResponse {
  samplesInCartsCount: number;
  pendingTransportsCount: number;
  pendingFinalWastesCount: number;
  anytimeWitnessFinalWastesCount: number;
  furtherReviewTransportsCount: number;
  blueFurtherReviewBagCount: number;
  furtherReviewFinalWastesCount: number;
  furtherReviewUnresolvedWastesCount: number;
  flaggedInterceptsCount: number;
  flaggedWastesCount: number;
}

export function getDrugLabel(item: Wasting, transloco: TranslocoService) {
  if (item.reasonForWaste === WastingReason.FoundMedication) return transloco.translate('Found Medication');

  if (item.genericName)
    return item.genericName; // should be MedicationGroup or Medication name description if mapped correctly
  else if (item.selectedMedicationGroup?.medication?.medicationDescription)
    return item.selectedMedicationGroup.medication.medicationDescription;
  else if (item.selectedMedicationGroup?.medicationGroupDescription)
    return item.selectedMedicationGroup.medicationGroupDescription;
  else return transloco.translate('Manually Entered Medication');
}

export function getTotalWasteAmount(item: Wasting, transloco: TranslocoService) {
  if (item.lot !== null && item.lot.wasteAmount.length > 0) {
    const totalAmount = item.lot.wasteAmount.reduce((a, b) => a + b, 0);
    return totalAmount === 0 ? transloco.translate('N/A') : totalAmount + ' ' + item.wasteUnits;
  } else {
    return item.wasteAmount === 0 ? transloco.translate('N/A') : item.wasteAmount + ' ' + item.wasteUnits;
  }
}

export function getWastingTypeText(item: Wasting, transloco: TranslocoService) {
  let str = transloco.translate('WastingType:' + item.wastingType);
  if (item.wastingType === WastingType.Standard) {
    if (item.standardWastingType === StandardWastingType.Default && item.viewMode === VIEWMode.Landscape)
      str += ' - ' + transloco.translate('ORProcedural');
    else if (item.standardWastingType > 0)
      str += ' - ' + transloco.translate('StandardWastingType:' + item.standardWastingType);
  }

  return str;
}

export enum DiversionRisk {
  Low,
  Medium,
  High,
}

export interface AIVideoReviewReason {
  reason: string;
  frameTimestamp: number;
}

export interface AIVideoReview {
  narrative: string;
  risk: DiversionRisk;
  reasons: AIVideoReviewReason[];
}

export interface Flagging {
  flagType: FlagType;
  flagDate: string;
  flagUserId?: number;
  flagUserName?: string;
  flagCleared: boolean;
  flagClearedUserId?: number;
  flagClearedUserName?: string;
}

export enum FlagType {
  AIReview,
  InPersonWitness,
  AnytimeWitness,
  System
}